import React from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  Download,
  Stethoscope,
  Shield,
  Snowflake,
  Wrench,
  GraduationCap,
  FileText,
  Sprout,
  Bug,
  HeartPulse,
  BookOpen,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import ProductFinder from "@/components/ProductFinder";
import SectionLabel from "@/components/SectionLabel";
import FormWithWhatsApp from "@/components/FormWithWhatsApp";
import { buildWaLink, WA_TEMPLATES } from "@/lib/whatsapp";
import { BAH_PRODUCT_CATALOGUE_PDF } from "@/lib/assets";
import { SPECIES, CHALLENGES } from "@/data/products";

const HUB_TILES = [
  {
    id: "search-species",
    title: "Search by species",
    body: "Find the right Biodeal product family for poultry, cattle, sheep & goats, pigs, camels, dogs & cats — or biosecurity and cold-chain.",
    Icon: Sprout,
    to: "/products",
  },
  {
    id: "search-challenge",
    title: "Search by challenge",
    body: "Match products to clinical and field challenges — respiratory, parasites, recovery, udder, wounds, biosecurity, cold chain.",
    Icon: Bug,
    to: "/solutions",
  },
  {
    id: "product-sheets",
    title: "Product sheets",
    body: "Request product sheets directly — composition, indications, pack sizes and field notes from the catalogue.",
    Icon: FileText,
    to: "/products",
  },
  {
    id: "responsible-use",
    title: "Responsible-use notes",
    body: "Guidance designed to support responsible antimicrobial use, residue awareness and farmer safety.",
    Icon: Shield,
    to: "/about",
  },
  {
    id: "instruments",
    title: "Instruments guide",
    body: "Burdizzo, balling guns, hoof, calving and identification tools — all from the BAH Instruments Catalogue 2026.",
    Icon: Wrench,
    to: "/instruments",
  },
  {
    id: "cold-chain",
    title: "Cold-chain support",
    body: "Vaccine refrigerators, carriers and electronic temperature loggers for clinics, institutions and outreach.",
    Icon: Snowflake,
    to: "/cold-chain",
  },
  {
    id: "cmt",
    title: "Training & CMT registration",
    body: "Book CMT sessions, vet training, agrovet product training, farmer education days and demonstrations.",
    Icon: GraduationCap,
    to: "/training",
  },
  {
    id: "field-support",
    title: "Technical WhatsApp support",
    body: "Talk to the Biodeal Animal Health technical team for clarifications, field cases and product selection support.",
    Icon: MessageCircle,
    href: buildWaLink(WA_TEMPLATES.technical_general + " "),
    external: true,
  },
];

export default function VetSupportHub() {
  return (
    <div data-testid="vet-support-hub-page">
      <PageHero
        testId="vet-hub-hero"
        eyebrow="Vet Support Hub"
        title="A serious working space for animal health professionals."
        lede="Product sheets, responsible-use notes, species-based product navigation, instruments, cold-chain, CMT registration and direct technical WhatsApp support — all in one place."
        image="https://images.pexels.com/photos/6235241/pexels-photo-6235241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{
          label: "WhatsApp Technical Team",
          href: buildWaLink(WA_TEMPLATES.technical_general + " "),
          external: true,
        }}
        secondaryCta={{ label: "Book CMT / Training", to: "/training" }}
      />

      {/* Tiles */}
      <section className="py-20 md:py-28 bg-bah-paper" data-testid="hub-tiles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>Hub modules</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
              Built for clinical and field work.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HUB_TILES.map((t) => {
              const inner = (
                <>
                  <div className="h-11 w-11 rounded-xl bg-bah-green/10 grid place-content-center text-bah-green">
                    <t.Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-xl text-bah-ink leading-snug">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-sm text-bah-mute leading-relaxed">{t.body}</p>
                </>
              );
              return t.href ? (
                <a
                  key={t.id}
                  href={t.href}
                  target={t.external ? "_blank" : undefined}
                  rel={t.external ? "noopener noreferrer" : undefined}
                  className="card-rise bg-white border border-bah-line rounded-2xl p-6 flex flex-col"
                  data-testid={`hub-tile-${t.id}`}
                >
                  {inner}
                </a>
              ) : (
                <Link
                  key={t.id}
                  to={t.to}
                  className="card-rise bg-white border border-bah-line rounded-2xl p-6 flex flex-col"
                  data-testid={`hub-tile-${t.id}`}
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Embedded Product Finder */}
      <section className="py-20 md:py-28 bg-bah-paperAlt" data-testid="hub-product-finder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionLabel>Find a product</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
              Filter by species and challenge.
            </h2>
            <p className="mt-4 text-bah-mute">
              A guidance tool — not a substitute for professional veterinary judgement.
            </p>
            <div className="mt-6 rounded-xl bg-white border border-bah-line p-5 text-sm text-bah-ink">
              <div className="flex items-center gap-2 text-bah-green font-semibold">
                <HeartPulse className="h-4 w-4" /> Responsible-use note
              </div>
              <p className="mt-2 text-bah-mute leading-relaxed">
                Do not use this tool to diagnose disease or finalise treatment
                decisions. For professional veterinary guidance, consult a
                qualified veterinarian or contact the Biodeal Animal Health
                technical team.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <ProductFinder />
          </div>
        </div>
      </section>

      {/* Technical Support Form */}
      <section className="py-20 md:py-28 bg-bah-paper" data-testid="hub-tech-support">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionLabel>Request technical clarification</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
              Talk to the Biodeal Animal Health technical team.
            </h2>
            <p className="mt-4 text-bah-mute">
              Submit a question, request a product sheet, or ask for technical
              support. The form goes to our technical team — or use WhatsApp for an
              instant conversation.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-bah-ink">
              {[
                "Clarifications on indication, dose form or pack size",
                "Species or challenge-specific product selection",
                "Field case support",
                "Cold-chain / vaccine handling questions",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <Stethoscope className="h-4 w-4 text-bah-green mt-0.5" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <FormWithWhatsApp
              endpoint="/forms/technical-support"
              testidPrefix="tech-support"
              submitLabel="Request Technical Clarification"
              successText="A member of our technical team will be in touch shortly."
              aiDraft={{ topic: "technical-support", targetField: "message", roleField: "role" }}
              waMessageBuilder={(v) =>
                `Hello Biodeal Animal Health technical team, I am ${v.name || "[name]"} (${v.role || "professional"}). ${
                  v.product_of_interest ? `Product of interest: ${v.product_of_interest}. ` : ""
                }${v.species ? `Species: ${v.species}. ` : ""}${
                  v.challenge ? `Challenge: ${v.challenge}. ` : ""
                }Message: ${v.message || ""}`
              }
              fields={[
                { name: "name", label: "Your name", required: true, placeholder: "Dr. Jane Mwangi" },
                {
                  name: "role",
                  label: "Role",
                  options: ["Veterinary professional", "Agrovet", "Farmer", "Distributor", "Institution", "Other"],
                },
                { name: "organization", label: "Organisation / Clinic", placeholder: "Optional" },
                { name: "county", label: "County / Location", placeholder: "e.g. Kiambu" },
                { name: "phone", label: "Phone / WhatsApp", required: true, placeholder: "+254…" },
                { name: "email", label: "Email", type: "email" },
                { name: "species", label: "Species", options: SPECIES.map((s) => s.label) },
                { name: "challenge", label: "Challenge", options: CHALLENGES.map((c) => c.label) },
                { name: "product_of_interest", label: "Product of interest", placeholder: "Optional — e.g. WORMIZAN SUPER" },
                {
                  name: "message",
                  label: "Your question / case",
                  required: true,
                  textarea: true,
                  rows: 5,
                  full: true,
                  placeholder: "Describe the case, herd, symptoms or product clarification you need…",
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Bottom CTAs */}
      <section className="py-20 bg-bah-ink text-white" data-testid="hub-bottom-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href={BAH_PRODUCT_CATALOGUE_PDF}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.07] p-6 transition-colors"
            data-testid="hub-cta-catalogue"
          >
            <Download className="h-5 w-5 text-bah-yellow" />
            <div className="mt-4 font-display text-xl">Download Product Catalogue</div>
            <div className="mt-2 text-sm text-white/70">The current Biodeal Animal Health catalogue (PDF).</div>
          </a>
          <Link
            to="/training"
            className="rounded-2xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.07] p-6 transition-colors"
            data-testid="hub-cta-training"
          >
            <BookOpen className="h-5 w-5 text-bah-yellow" />
            <div className="mt-4 font-display text-xl">Book CMT / Training</div>
            <div className="mt-2 text-sm text-white/70">Veterinary, agrovet, farmer and instrument training sessions.</div>
          </Link>
          <a
            href={buildWaLink(WA_TEMPLATES.technical_general + " ")}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border border-[#25D366]/40 bg-[#25D366]/10 hover:bg-[#25D366]/15 p-6 transition-colors"
            data-testid="hub-cta-whatsapp"
          >
            <MessageCircle className="h-5 w-5 text-[#25D366]" />
            <div className="mt-4 font-display text-xl">WhatsApp Technical Team</div>
            <div className="mt-2 text-sm text-white/70">Instant conversation with the BAH technical team.</div>
          </a>
        </div>
      </section>
    </div>
  );
}
